import type { LabeledEdgeProps } from "@/pages/canvas/types/canvasNodeData";
import {
  getFloatingEdgeParams,
  sideFromData,
} from "@/pages/canvas/utils/floatingEdge";
import {
  buildLinePath,
  type LineGeometry,
  type LinePoint,
  routeFrame,
  settleStepPoints,
} from "@/pages/canvas/utils/linePath";
import {
  BaseEdge,
  EdgeLabelRenderer,
  useInternalNode,
  type XYPosition,
} from "@xyflow/react";
import { useState } from "react";
import { useCanvasApi } from "../context/CanvasContext";
import { useCanvasUi } from "../context/CanvasUiContext";
import { LineLabelEditor } from "./shared/LineLabel";
import { LineRouteHandles } from "./shared/LineRouteHandles";

export type {
  CanvasLabeledEdge,
  LabeledEdgeData,
} from "@/pages/canvas/types/canvasNodeData";

/**
 * Custom RF edge type: a straight, elbow or curved path between two nodes,
 * an optional label at its midpoint, and, while selected, the grips that
 * route it. Each end attaches automatically to the side facing the other
 * node, unless the user dropped it on a side, which pins it. Registered as
 * `edgeTypes={{ labeled: LabeledEdge }}` in CanvasPage.
 */
export function LabeledEdge({
  id,
  source,
  target,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  markerStart,
  markerEnd,
  style,
  label,
  selected,
}: LabeledEdgeProps) {
  const edgeData = data ?? {};
  const curve = edgeData.curve_type ?? "step";
  const sourceSide = sideFromData(edgeData.source_side);
  const targetSide = sideFromData(edgeData.target_side);

  // Attachment re-derives from live node geometry every frame (see
  // floatingEdge.ts); falls back to RF's handle-derived coords until the
  // nodes are measured.
  const sourceNode = useInternalNode(source);
  const targetNode = useInternalNode(target);
  const floating = getFloatingEdgeParams(sourceNode, targetNode, {
    sourceSide,
    targetSide,
  });

  // Live overrides while a grip is being dragged; storage gets the release.
  const [liveBend, setLiveBend] = useState<XYPosition | null | undefined>();
  const [livePoints, setLivePoints] = useState<LinePoint[] | undefined>();
  const [liveEnd, setLiveEnd] = useState<{
    end: "source" | "target";
    at: XYPosition;
  } | null>(null);

  const geometry: LineGeometry = {
    curve,
    source: {
      x: floating?.sx ?? sourceX,
      y: floating?.sy ?? sourceY,
      side: floating?.sourcePos ?? sourcePosition,
    },
    target: {
      x: floating?.tx ?? targetX,
      y: floating?.ty ?? targetY,
      side: floating?.targetPos ?? targetPosition,
    },
    bend: liveBend !== undefined ? liveBend : (edgeData.bend ?? null),
    points: livePoints ?? (edgeData.points as LinePoint[] | undefined) ?? null,
    pointsFrame: edgeData.points_frame ?? null,
    labelled: typeof label === "string" && label.length > 0,
  };
  if (liveEnd) {
    geometry[liveEnd.end] = { ...geometry[liveEnd.end], ...liveEnd.at };
  }
  const built = buildLinePath(geometry);

  const { canEdit, updateEdge } = useCanvasApi();
  const { editingEdgeId, setEditingEdgeId } = useCanvasUi();
  const isEditing = editingEdgeId === id;
  // The grips step out of the way while the label is being typed.
  const showHandles = canEdit && selected && !isEditing;
  const labelText = typeof label === "string" ? label : "";
  const showLabel = !!labelText || isEditing;

  return (
    <>
      <BaseEdge
        id={id}
        path={built.path}
        markerStart={markerStart}
        markerEnd={markerEnd}
        style={style}
      />
      {showHandles && (
        <LineRouteHandles
          geometry={geometry}
          built={built}
          onBendChange={(bend, final) => {
            if (!final) {
              setLiveBend(bend);
              return;
            }
            setLiveBend(undefined);
            updateEdge(id, { bend });
          }}
          onPointsChange={(points, final) => {
            if (!final) {
              setLivePoints(points);
              return;
            }
            setLivePoints(undefined);
            const settled = settleStepPoints(geometry, points);
            updateEdge(id, {
              points: settled,
              points_frame: settled ? routeFrame(geometry) : null,
            });
          }}
          onEndpointDrag={(end, at) => setLiveEnd(at ? { end, at } : null)}
          onEndpointDrop={(end, drop) => {
            if (!drop) return;
            updateEdge(id, {
              [end]: drop.nodeId,
              [`${end}_side`]: drop.side,
              // A new attachment invalidates a hand-routed polyline.
              points: null,
              points_frame: null,
            });
          }}
        />
      )}
      {showLabel && (
        <EdgeLabelRenderer>
          <div
            className="nodrag nopan absolute"
            style={{
              transform: `translate(-50%, -50%) translate(${built.label.x}px, ${built.label.y}px)`,
              pointerEvents: "all",
            }}
          >
            <LineLabelEditor
              label={labelText}
              labelStyle={edgeData.label_style}
              editing={isEditing}
              canEdit={canEdit}
              midpoint={built.label}
              onStartEdit={() => setEditingEdgeId(id)}
              onEndEdit={() => setEditingEdgeId(null)}
              onCommit={(text) => updateEdge(id, { label: text })}
              onStyleChange={(label_style) => updateEdge(id, { label_style })}
            />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
